import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Grid,
} from "@mui/material";
import {
  Home as HomeIcon,
  AppRegistration as RegisterIcon,
  Login as LoginIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { login } from "@smfa/utils/src";

/**
 * User Login
 * @returns {JSX.Element}
 * @component
 */
const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");

  // Initialize isLoading to false. It should only be true during form submission.
  const [isLoading, setIsLoading] = useState(false);

  const auth = useSelector((state) => state.auth) || {};
  const isAuthenticated = Boolean(auth?.isAuthenticated);

  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  useEffect(() => {
    // If already authenticated, redirect to member dashboard or redirect param
    if (isAuthenticated) {
      navigate(redirect || "/member-dashboard");
    }
  }, [isAuthenticated, navigate, redirect]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await login(formData, {
        dispatch,
        onSuccess: ({ token, user }) => {
          log.debug("Login successful, user data:", token, user);
          navigate(redirect || "/member-dashboard");
        },
        onError: ({ message, response }) => setError(message),
      });
    } catch (err) {
      log.error("Login error:", err);
      setError(
        err.response?.data?.message || "An error occurred during login.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 5, mb: 5 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Member Login
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={formData.email}
            onChange={handleChange}
            disabled={isLoading}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            disabled={isLoading}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={isLoading}
            startIcon={<LoginIcon />}
          >
            {isLoading ? (
              <React.Fragment>
                <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
                Signing In...
              </React.Fragment>
            ) : (
              "Sign In"
            )}
          </Button>

          {/* <Grid container justifyContent="center">
            <Grid>
              <Link to="/forgot-password" style={{ textDecoration: "none" }}>
                <Typography variant="body2" color="primary">
                  Forgot Password?
                </Typography>
              </Link>
            </Grid>
          </Grid> */}

          <Grid container mt={4} spacing={3}>
            <Grid size={12} textAlign="center">
              <Typography variant="body2">
                Don't have an account?&nbsp;&nbsp;
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={() => navigate("/signup")}
                  startIcon={<RegisterIcon />}
                >
                  Register
                </Button>
              </Typography>
            </Grid>
            <Grid size={12} textAlign="center">
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/")}
                startIcon={<HomeIcon />}
              >
                Home
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;
